<template>
    <BaseCard
        :theme="Theme.Dark"
        class="relative h-full overflow-hidden xl:!px-16 xl:!py-12"
        :data-spec-class="dataSpecClass"
    >
        <div class="relative z-20 max-w-2xl">
            <h1 class="mb-3 text-xl xl:text-2xl">
                {{ title }}
            </h1>
            <div class="mb-6 xl:text-xl">
                <slot />
            </div>

            <div class="text-sm text-white/80">
                <slot name="note">
                    {{ t('pages.InvoicePaymentSuccessPage.thankYouSection.note') }}
                </slot>
            </div>

            <img
                v-if="showLogo"
                class="mt-6 h-8"
                src="/img/23M-Logo.svg"
                alt="23M Logo"
            />
        </div>

        <div
            v-show="gradientLoaded"
            class="payment-success-gradient"
        ></div>

        <div
            v-if="$slots.actions"
            class="relative z-20 mb-6 flex flex-col gap-4 sm:flex-row sm:justify-center"
        >
            <slot name="actions" />
        </div>
    </BaseCard>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue';
import { Theme } from '@/types';
import BaseCard from './BaseCard.vue';
import { useI18n } from 'vue-i18n';

interface Props {
    title: string;
    showLogo?: boolean;
    dataSpecClass?: string;
}

const { t } = useI18n();

withDefaults(defineProps<Props>(), {
    showLogo: true,
    dataSpecClass: 'payment-success-card',
});

const gradientLoaded = ref(false);

onMounted(() => {
    const gradientImg = new Image();
    const maskImg = new Image();

    gradientImg.onload = () => {
        maskImg.onload = () => {
            gradientLoaded.value = true;
        };
        maskImg.src = '/img/curve-header.svg';
    };
    gradientImg.src = '/img/gradient.jpg';
});
</script>

<style scoped>
.payment-success-gradient {
    position: absolute;
    top: 0;
    bottom: 0;
    right: 0;
    width: 350px;
    mask-position: 0% 50%;
    mask-image: url('/img/curve-header.svg');
    mask-size: cover;
    z-index: 10;
    opacity: 0;
    transform: translateX(30px);
    animation: fadeInGradient 1.2s cubic-bezier(0.25, 0.46, 0.45, 0.94) forwards;
}

.payment-success-gradient::before {
    content: '';
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    background-image: url('/img/gradient.jpg');
    background-position: 100% 75%;
    background-size: 200% 200%;
    background-repeat: no-repeat;
    animation: background-move ease-out 30s infinite;
}

@keyframes fadeInGradient {
    0% {
        opacity: 0;
        transform: translateX(30px);
    }
    100% {
        opacity: 1;
        transform: translateX(0);
    }
}

@keyframes background-move {
    16% {
        background-position: 75% 0;
    }
    33% {
        background-position: 25% 50%;
    }
    50% {
        background-position: 100% 25%;
    }
    67% {
        background-position: 75% 100%;
    }
    83% {
        background-position: 0 0;
    }
}

@media (max-width: 1024px) {
    .payment-success-gradient {
        width: 150px;
    }
}
</style>
